import { useNavigate } from 'react-router-dom'
import * as Styled from './styled'
import { capitalize } from '~utils/text'
import { useGlobals, useAmplitude } from '~hooks'
import { FuelTypography, FuelTag, FuelDivider, FuelGridItem } from '@frete.com/fuel-react'
import { date } from '~utils/date'
import { CalloutConsumption, ConsumptionSkeleton } from './components'
import { Skeleton } from '~components'
import { Props } from './interfaces'
import { applyRenewDay } from './utils'
import { getCustomToken } from '~utils/token'

const RISK_MANAGEMENT_PRODUCTS = ['Consulta GR', 'Cadastro GR']
const PRODUCTS_TO_SHOW = ['Usuário', 'Frete Destaque', 'Cadastro Frete Plus', 'Motoristas sugeridos']
const PRODUCTS_ORDER = { Usuário: 0, 'Frete Destaque': 1, 'Cadastro Frete Plus': 2, 'Motoristas sugeridos': 3 }

const ConsumptionCard = ({ isLoading, data }: Props) => {
  const navigate = useNavigate()
  const { userData: user } = useGlobals()
  const { sendAmplitude } = useAmplitude()

  const sendToAmplitude = (event: string) => {
    const eventProps = {
      company_id: user.company.id,
      user_name: user.name,
      user_role: user.typeDescription,
      company_plan: user.company.plan,
      user_id: user.id,
    }

    sendAmplitude(event, { ...eventProps })
  }

  const handleClickInvoice = () => {
    sendToAmplitude('consumption-products-click-invoice')
    window.open(data.invoice?.url, '_blank')
  }

  const showPlansModal = () => {
    sendToAmplitude('consumption-products-click-plans')
    navigate('/consumo-de-produtos/meus-planos')
  }

  const hasUserData = !!user
  const renewDay = user?.company?.combo?.renew_day
  const renewDayISOString = renewDay ? applyRenewDay(renewDay) : null

  const userProducts = user?.company?.combo?.products
    .filter((product) => PRODUCTS_TO_SHOW.includes(product?.name.trim()))
    .sort((a, b) => PRODUCTS_ORDER[a.name.trim()] - PRODUCTS_ORDER[b.name.trim()])

  const dataObject = {
    month: capitalize(date(new Date().toISOString(), 'MMMM')),
    plan: hasUserData ? user?.company?.combo?.name?.toLowerCase() : '',
    renew_day: renewDay ? date(renewDayISOString, 'dd/MM/yy') : '',
    products: hasUserData ? userProducts : [],
    extras: data?.products?.length ? data?.products : [],
    riskManagement:
      data?.products
        ?.filter((product) => RISK_MANAGEMENT_PRODUCTS.includes(product.name))
        .map((risk) => ({
          ...risk,
          name: `${risk.name
            .replace(' GR', '')
            .toLowerCase()
            .concat(risk.amount > 1 ? 's' : '')} `,
        })) || [],
  }

  return (
    <Styled.Wrapper>
      <Styled.Title isLoading={isLoading}>
        <Skeleton
          $width="50%"
          $height="35px"
          isLoading={isLoading}
          Component={
            <FuelTypography size="body-lg" weight="bold">
              Consumo de {dataObject.month}
            </FuelTypography>
          }
        />

        <Skeleton
          $width="50%"
          $height="20px"
          isLoading={isLoading}
          Component={
            <FuelTypography size="caption" color="neutral-default">
              Seu plano{' '}
              <span
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={showPlansModal}
                data-testid="consumption-card-plan-link"
              >
                {dataObject.plan}
              </span>{' '}
              renova em {dataObject.renew_day}
            </FuelTypography>
          }
        />
      </Styled.Title>

      <Styled.ContentWrapper>
        <Styled.Content>
          {isLoading ? (
            <ConsumptionSkeleton />
          ) : (
            <>
              {dataObject.products.map((consumptionProducts, itemProducts) => (
                <FuelGridItem key={getCustomToken()}>
                  <FuelTypography size="body-md" weight="bold" spacingTop={itemProducts !== 0 ? 16 : 0}>
                    {consumptionProducts?.name}
                  </FuelTypography>

                  <Styled.ConsumptionData style={{ marginBottom: 16 }}>
                    <FuelTypography size="body-sm" weight="regular">
                      {consumptionProducts?.consumption.consumed - consumptionProducts?.consumption?.extras} /{' '}
                      {consumptionProducts?.consumption.initial} inclusos no plano
                    </FuelTypography>

                    {dataObject?.extras.map(
                      (extra) =>
                        extra.name === consumptionProducts.name && (
                          <FuelTag
                            uppercase={false}
                            label={`${extra.amount} extras = R$${extra.total} `}
                            key={getCustomToken()}
                          />
                        ),
                    )}
                  </Styled.ConsumptionData>

                  {itemProducts !== dataObject.products.length - 1 && <FuelDivider orientation="horizontal" />}
                </FuelGridItem>
              ))}

              <>
                {dataObject.products.length > 0 && <FuelDivider orientation="horizontal" />}

                <FuelTypography size="body-md" weight="bold" spacingTop={16}>
                  Gerenciamento de risco
                </FuelTypography>

                <Styled.ConsumptionData>
                  {dataObject?.riskManagement?.length > 0 ? (
                    dataObject?.riskManagement?.map((risk) => (
                      <FuelTag
                        uppercase={false}
                        label={`${risk.amount} ${risk.name} = R$${risk.total} `}
                        key={getCustomToken()}
                      />
                    ))
                  ) : (
                    <FuelTypography color="neutral-default">Nenhum produto adquirido</FuelTypography>
                  )}
                </Styled.ConsumptionData>
              </>
            </>
          )}
        </Styled.Content>
      </Styled.ContentWrapper>

      {!isLoading && dataObject?.extras.length > 0 && (
        <CalloutConsumption onClickSeeInvoice={handleClickInvoice} invoice={data.invoice} value={data?.total_price} />
      )}
    </Styled.Wrapper>
  )
}

export default ConsumptionCard
